<script setup>
import { THEMES } from '@/constants';
import { Link, usePage } from "@inertiajs/vue3";
import { ref, computed } from "vue";

const page = usePage();
const context = computed(() => page.props?.context);
const theme = context.value.theme;

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (v) =>
        [
          "primary",
          "secondary",
          "tertiary",
          "outlined",
          "no-style",
          "pagination",
          "link"
        ].indexOf(v) !== -1,
  },
  type: {
    type: String,
    default: "button",
    validator: (v) =>
        [
          "button",
          "link",
        ].indexOf(v) !== -1,
  },
  link: {
    type: String,
    required: false,
  },
  external: {
    type: Boolean,
    default: false,
  },
  linkData: {
    type: String,
    required: false,
  },
  ariaLabel: {
    type: String,
    required: false,
  }
});

const buttonType = computed(() => {
  return props.external ? "a" : "link" == props.type ? Link : "button"; 
});

const variantThemes = ref({
  [THEMES.STUDY_AUS]: {
    "primary": "bg-primary text-white text-center font-au-sans-medium border-2 border-solid border-primary rounded-3xl py-2 px-4 hover:bg-white hover:text-primary",
    "secondary": "bg-primary-500 text-center text-primary font-au-sans-medium border-2 border-solid border-primary-500 hover:bg-white rounded-3xl py-2 px-4",
    "outlined": "bg-transparent text-center border-2 border-primary font-au-sans-medium border-solid text-primary hover:bg-primary-500 rounded-3xl py-2 px-4",
    "no-style": "h-fit w-fit text-left bg-transparent text-primary border-2 border-transparent border-solid",
    "pagination": "text-center my-auto w-10 h-10 text-primary flex items-center justify-center rounded-full",
    "link": "bg-transparent text-center text-primary font-au-sans-medium border-0 rounded-3xl py-2 px-4 hover:underline hover:text-austrade"
  },
  [THEMES.STUDY_MELB]: {
    "primary": "bg-primary-500 text-center text-white font-au-sans-medium rounded-3xl py-2 px-6 outline-offset-2 hover:bg-primary-200",
    "secondary": "bg-primary text-white text-center font-au-sans-medium rounded-3xl py-2 px-6 outline-offset-2 hover:bg-blue-500",
    "tertiary": "bg-blue-200 text-primary uppercase text-center font-au-sans-medium rounded-3xl py-2 px-5 hover:bg-blue-100",
    "outlined": "bg-transparent text-center text-primary font-au-sans-regular border-0 rounded-3xl py-2 px-4 hover:underline",
    "no-style": "h-fit w-fit text-left bg-transparent text-white border-2 border-transparent border-solid",
    "pagination": "text-center my-auto w-10 h-10 text-primary flex items-center justify-center rounded-full",
    "link": "bg-transparent text-center text-inherit font-au-sans-regular border-0 rounded-3xl py-2 hover:underline",
    "search" : "bg-blue-200 text-center font-au-sans-medium font-semibold rounded-3xl py-2 px-8 hover:bg-blue-100 md:text-[16px] uppercase"
  },
  [THEMES.STUDY_QLD]: {
    "primary": "bg-primary-700 text-center text-white font-au-sans-medium rounded-3xl py-2 px-6 hover:bg-primary-600",
    "secondary": "bg-black-500 text-white text-center font-au-sans-medium rounded-3xl py-2 px-6 hover:bg-black-200",
    "tertiary": "bg-blue-200 text-white text-center font-au-sans-medium rounded-3xl py-2 px-5 hover:bg-blue-500",
    "outlined": "bg-transparent text-center text-blue-200 font-au-sans-regular border-0 rounded-3xl py-2 px-4 hover:underline",
    "no-style": "h-fit w-fit text-left bg-transparent text-primary border-2 border-transparent border-solid",
    "pagination": "text-center my-auto w-10 h-10 text-primary flex items-center justify-center rounded-full",
    "link": "bg-transparent text-center text-blue-200 font-au-sans-regular border-0 rounded-3xl py-2 px-4 hover:underline"
  },
});
</script>

<template>
  <component
    :is="buttonType"
    :role="type"
    :aria-label="ariaLabel"
    :data="linkData"
    :target="external ? '_blank' : '_self'"
    :href="link"
    class="inline-block transition duration-150"
    :class="[variantThemes[theme][variant], type == 'link' ? 'no-underline' : '']"
  >
    <slot />
  </component>
</template>
